import { useIsGuestCheckout } from '@/composables/useIsGuestCheckout'
import {
  CHECKOUT_GUEST_STEPS,
  CHECKOUT_STEPS
} from '@/constants/CheckoutConstants.js'
import { ValidationHelper } from '@/services/helpers/ValidationHelper'
import { useAddressesStore } from '@/stores/addresses.ts'
import { useCartStore } from '@/stores/cart.js'
import { useCustomerStore } from '@/stores/customer'
import { useGuestCheckoutStore } from '@/stores/guestCheckout'
import { usePaymentStore } from '@/stores/payment.ts'
import { useRegularCheckoutStore } from '@/stores/regularCheckout'
import { storeToRefs } from 'pinia'
import { computed, unref } from 'vue'
import { useRoute } from 'vue-router'

export function useCheckoutSteps() {
  const customerStore = useCustomerStore()
  const regularCheckoutStore = useRegularCheckoutStore()
  const addressesStore = useAddressesStore()
  const payment = usePaymentStore()
  const cartStore = useCartStore()
  const guestStore = useGuestCheckoutStore()
  const { isGuestCheckout } = useIsGuestCheckout()

  const route = useRoute()

  const { hasPaymentMethods } = storeToRefs(payment)
  const { hasAddresses, addresses } = storeToRefs(addressesStore)
  const { deliveryOptions } = storeToRefs(cartStore)
  const { profileV2 } = storeToRefs(customerStore)

  // Use appropriate store based on checkout type
  const selectedShippingAddressId = computed(() => {
    return isGuestCheckout.value
      ? null
      : storeToRefs(regularCheckoutStore).selectedShippingAddressId.value
  })

  const selectedBillingAddressId = computed(() => {
    return isGuestCheckout.value
      ? null
      : storeToRefs(regularCheckoutStore).selectedBillingAddressId.value
  })

  const selectedShippingAddressData = computed(() => {
    return isGuestCheckout.value
      ? guestStore.selectedShippingAddressData
      : storeToRefs(regularCheckoutStore).selectedShippingAddressData.value
  })

  const selectedBillingAddressData = computed(() => {
    return isGuestCheckout.value
      ? guestStore.selectedBillingAddressData
      : storeToRefs(regularCheckoutStore).selectedBillingAddressData.value
  })

  const useSameBillingAddress = computed(() => {
    return isGuestCheckout.value
      ? guestStore.useSameBillingAddress
      : storeToRefs(regularCheckoutStore).useSameBillingAddress.value
  })

  const selectedPaymentMethodId = computed(() => {
    return isGuestCheckout.value
      ? null
      : storeToRefs(regularCheckoutStore).selectedPaymentMethodId.value
  })

  const selectedPaymentMethodData = computed(() => {
    return isGuestCheckout.value
      ? guestStore.selectedPaymentMethodData
      : storeToRefs(regularCheckoutStore).selectedPaymentMethodData.value
  })

  const selectedDeliveryOptionsMap = computed(() => {
    return isGuestCheckout.value
      ? guestStore.selectedDeliveryOptionsMap
      : storeToRefs(regularCheckoutStore).selectedDeliveryOptionsMap.value
  })

  const hasMoreThanOneDeliveryOption = computed(() => {
    return deliveryOptions.value?.some(
      (vendor) => vendor.delivery_options && vendor.delivery_options.length > 1
    )
  })

  const hasCarrierSelected = computed(
    () =>
      !!profileV2.value?.preferred_carrier || !!selectedDeliveryOptionsMap.value
  )

  const hasAddressesSelected = computed(() => {
    // For address IDs, we trust they reference complete addresses in the database
    // For address data objects, we need to verify they are complete
    const hasCompleteShippingAddress = !!(
      selectedShippingAddressId.value ||
      (selectedShippingAddressData.value &&
        ValidationHelper.isAddressComplete(selectedShippingAddressData.value))
    )

    const hasCompleteBillingAddress = !!(
      selectedBillingAddressId.value ||
      (selectedBillingAddressData.value &&
        ValidationHelper.isAddressComplete(selectedBillingAddressData.value))
    )

    return useSameBillingAddress.value
      ? hasCompleteShippingAddress
      : hasCompleteShippingAddress && hasCompleteBillingAddress
  })

  const hasPaymentMethodSelected = computed(() => {
    return !!selectedPaymentMethodId.value || !!selectedPaymentMethodData.value
  })

  const getDefaultAddressStep = () => {
    const steps = isGuestCheckout.value ? CHECKOUT_GUEST_STEPS : CHECKOUT_STEPS

    // For guest checkout, always go to add page (skip select view)
    if (isGuestCheckout.value) {
      if (
        hasAddressesSelected.value &&
        route.name !== steps.ADDRESS_TYPE &&
        route.name !== steps.ADDRESS_ACTION
      ) {
        return null
      }

      if (route.name === steps.ADDRESS_ACTION) {
        return steps.ADDRESS_ACTION
      }

      return steps.ADDRESS_ACTION
    }

    // For authenticated users, use the original logic
    if (hasAddressesSelected.value && route.name !== steps.ADDRESS_TYPE) {
      return null
    }

    if (!hasAddresses.value && route.name !== steps.ADDRESS_ACTION) {
      return steps.ADDRESS_ACTION
    }

    if (route.name === steps.ADDRESS_ACTION) {
      return steps.ADDRESS_ACTION
    }

    return steps.ADDRESS_TYPE
  }

  const addressType = computed(() => {
    // For address IDs, we trust they reference complete addresses in the database
    // For address data objects, we need to verify they are complete
    const hasCompleteShippingAddress = !!(
      selectedShippingAddressId.value ||
      (selectedShippingAddressData.value &&
        ValidationHelper.isAddressComplete(selectedShippingAddressData.value))
    )

    const hasCompleteBillingAddress = !!(
      selectedBillingAddressId.value ||
      (selectedBillingAddressData.value &&
        ValidationHelper.isAddressComplete(selectedBillingAddressData.value))
    )

    if (!hasCompleteShippingAddress) {
      return 'shipping'
    }
    if (!useSameBillingAddress.value && !hasCompleteBillingAddress) {
      return 'billing'
    }
    return null
  })

  const getDefaultPaymentStep = () => {
    const steps = isGuestCheckout.value ? CHECKOUT_GUEST_STEPS : CHECKOUT_STEPS

    if (!hasPaymentMethodSelected.value) {
      return steps.PAYMENT_ACTION
    }

    if (route.name === steps.PAYMENT_ACTION) {
      return steps.PAYMENT_ACTION
    }

    return route.name !== steps.PAYMENT ? null : steps.PAYMENT
  }

  const getDefaultDeliveryStep = () => {
    const steps = isGuestCheckout.value ? CHECKOUT_GUEST_STEPS : CHECKOUT_STEPS

    if (!hasCarrierSelected.value && hasMoreThanOneDeliveryOption.value) {
      return steps.DELIVERY
    }
    return route.name !== steps.DELIVERY ? null : steps.DELIVERY
  }

  const getDefaultStep = () => {
    const steps = isGuestCheckout.value ? CHECKOUT_GUEST_STEPS : CHECKOUT_STEPS
    const addressStep = getDefaultAddressStep()
    const paymentStep = getDefaultPaymentStep()
    return addressStep ?? paymentStep ?? steps.REVIEW
  }

  // Step completion status
  const isAddressStepCompleted = computed(() => hasAddressesSelected.value)
  const isPaymentStepCompleted = computed(() => hasPaymentMethodSelected.value)
  const isDeliveryStepCompleted = computed(
    () =>
      hasCarrierSelected.value &&
      isAddressStepCompleted.value &&
      isPaymentStepCompleted.value
  )
  const isReviewStepCompleted = computed(() => false)

  // Step availability
  const isAddressStepAvailable = computed(() => true) // Always available
  const isPaymentStepAvailable = computed(() => isAddressStepCompleted.value)
  const isDeliveryStepAvailable = computed(
    () => isAddressStepCompleted.value && isPaymentStepCompleted.value
  )
  const isReviewStepAvailable = computed(
    () =>
      isAddressStepCompleted.value &&
      isPaymentStepCompleted.value &&
      isDeliveryStepCompleted.value
  )

  const disableRiskyPaymentMethods = () => {
    if (!isGuestCheckout.value) {
      const billingAddressId = unref(
        regularCheckoutStore.selectedBillingAddressId
      )
      const shippingAddressId = unref(
        regularCheckoutStore.selectedShippingAddressId
      )
      if (billingAddressId === shippingAddressId) {
        return
      }
      const selectedPaymentMethodId = unref(
        regularCheckoutStore.selectedPaymentMethodId
      )
      const paymentMethod =
        unref(regularCheckoutStore.selectedPaymentMethodData) ??
        (selectedPaymentMethodId != null
          ? payment.getPaymentMethodById(Number(selectedPaymentMethodId))
          : null)
      const completedOrders = Number(
        profileV2.value?.completed_orders_count ?? 0
      )
      if (
        paymentMethod?.method_code === 'purchase_order' &&
        completedOrders === 0
      ) {
        regularCheckoutStore.selectedPaymentMethodId = null
        regularCheckoutStore.selectedPaymentMethodData = null
      }
      return
    }

    const shipping = guestStore.selectedShippingAddressData
    const billing = guestStore.selectedBillingAddressData
    const useSame = guestStore.useSameBillingAddress

    if (useSame) {
      return
    }

    const addressesAreSame =
      shipping &&
      billing &&
      shipping.street_name === billing.street_name &&
      shipping.street_number === billing.street_number &&
      shipping.zip === billing.zip &&
      shipping.city === billing.city &&
      shipping.country === billing.country

    if (addressesAreSame) {
      return
    }

    const paymentMethod = guestStore.selectedPaymentMethodData
    if (paymentMethod && paymentMethod.method_code === 'purchase_order') {
      guestStore.selectedPaymentMethodData = null
    }
  }

  return {
    // Computed properties
    hasMoreThanOneDeliveryOption,
    hasCarrierSelected,
    hasAddressesSelected,
    hasPaymentMethodSelected,
    addressType,

    // Step completion status
    isAddressStepCompleted,
    isPaymentStepCompleted,
    isDeliveryStepCompleted,
    isReviewStepCompleted,

    // Step availability
    isAddressStepAvailable,
    isPaymentStepAvailable,
    isDeliveryStepAvailable,
    isReviewStepAvailable,

    // Methods
    getDefaultAddressStep,
    getDefaultPaymentStep,
    getDefaultDeliveryStep,
    getDefaultStep,
    disableRiskyPaymentMethods,

    // Store refs for external access if needed
    selectedShippingAddressId,
    selectedBillingAddressId,
    useSameBillingAddress,
    selectedPaymentMethodId,
    selectedDeliveryOptionsMap,
    deliveryOptions,
    hasPaymentMethods,
    hasAddresses
  }
}
